import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { Query } from '@tanstack/react-query';
import type { OfflineStorage } from './storage';
import { visitaKeys } from '../hooks/visite';
import { VISITE_OFFLINE_CACHE_KEY } from './constants';

// Roots derived from visitaKeys (single source of truth): 'visite' for the
// lists / per-client agenda, 'visita' for a single record.
const VISITE_LIST_ROOT = visitaKeys.all[0];
const VISITA_DETAIL_ROOT = visitaKeys.detail(undefined)[0];

/**
 * Build a React Query persister backed by an app-provided {@link OfflineStorage}
 * adapter (localStorage on web, AsyncStorage on mobile). Reusable from both apps;
 * neither the persister nor this package touches platform storage directly.
 */
export function createVisiteOfflinePersister(storage: OfflineStorage) {
  return createAsyncStoragePersister({
    storage,
    key: VISITE_OFFLINE_CACHE_KEY,
    // Coalesce bursts of cache writes into at most one persist per second.
    throttleTime: 1000,
  });
}

/**
 * Persist ONLY successful Visite queries (lists, per-client agenda, detail) —
 * not the rest of the app's cache. Keeps the snapshot small and avoids writing
 * unrelated, possibly sensitive data into browser storage. Pass as
 * `persistOptions.dehydrateOptions.shouldDehydrateQuery`.
 */
export function shouldPersistVisiteQuery(query: Query): boolean {
  if (query.state.status !== 'success') return false;
  const root = query.queryKey[0];
  return root === VISITE_LIST_ROOT || root === VISITA_DETAIL_ROOT;
}

/**
 * Remove the persisted visite snapshot from the given storage. Multi-tenant
 * hygiene: the app MUST call this whenever the session ends or the tenant
 * changes (logout, failed refresh, stop-impersonation) so one tenant's visite
 * data does not linger in the browser for the next user.
 */
export async function clearVisiteOfflineCache(storage: OfflineStorage): Promise<void> {
  await storage.removeItem(VISITE_OFFLINE_CACHE_KEY);
}
